import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Reveal } from '../Reveal';
import { Note, UnderlineMark } from '../Marks';
import { StampBadge } from '../StampBadge';

/**
 * A hand-drawn mind map of everything that adds up to a good impression.
 * It draws itself in when it scrolls into view: branches first, then the words.
 */
interface Node {
  label: string;
  sub: string;
  side: 'left' | 'right';
  /** Vertical position in the SVG's coordinate space. */
  y: number;
  color: string;
  /** Small tilt so nothing sits perfectly level. */
  tilt: number;
}
const ROD = '#E42616';
const BLAA = '#2015B8';
const NODES: Node[] = [{
  label: 'Miljön',
  sub: 'Ljuset, borden, ljudnivån',
  side: 'left',
  y: 58,
  color: ROD,
  tilt: -1.6
}, {
  label: 'Doften',
  sub: 'Det du möts av i dörren',
  side: 'left',
  y: 202,
  color: BLAA,
  tilt: 1.2
}, {
  label: 'Bemötandet',
  sub: 'Någon som faktiskt bryr sig',
  side: 'left',
  y: 346,
  color: ROD,
  tilt: -0.9
}, {
  label: 'Råvaran',
  sub: 'I säsong, från trakten',
  side: 'left',
  y: 490,
  color: BLAA,
  tilt: 1.7
}, {
  label: 'Ärligheten',
  sub: 'Rätt pris för det du får',
  side: 'left',
  y: 634,
  color: ROD,
  tilt: -1.1
}, {
  label: 'Tåligheten',
  sub: 'Lika bra en tisdag i november',
  side: 'left',
  y: 778,
  color: BLAA,
  tilt: 1.5
}, {
  label: 'Hantverket',
  sub: 'Gjort för hand, varje dag',
  side: 'right',
  y: 58,
  color: BLAA,
  tilt: 1.4
}, {
  label: 'Detaljerna',
  sub: 'Porslinet, brödet, smöret',
  side: 'right',
  y: 202,
  color: ROD,
  tilt: -1.3
}, {
  label: 'Signaturen',
  sub: 'Något bara de gör',
  side: 'right',
  y: 346,
  color: BLAA,
  tilt: 1.1
}, {
  label: 'Första tuggan',
  sub: 'Där det avgörs',
  side: 'right',
  y: 490,
  color: ROD,
  tilt: -1.5
}, {
  label: 'Mästerverket',
  sub: 'Det du berättar om efteråt',
  side: 'right',
  y: 634,
  color: BLAA,
  tilt: 1.3
}, {
  label: 'Suget',
  sub: 'Du vill tillbaka direkt',
  side: 'right',
  y: 778,
  color: ROD,
  tilt: -1.8
}];
const CX = 450;
const CY = 418;
const RX = 168;
const RY = 82;

/** Deterministic wobble so the drawing looks hand-made but never re-rolls. */
const jitter = (seed: number, amount: number) => Math.sin(seed * 12.9898) * amount;

/**
 * A branch that leaves the edge of the stamp and wanders out to its word.
 * Starts clear of the stamp so nothing crosses it.
 */
function branch(node: Node, index: number) {
  const endX = node.side === 'left' ? 292 : 608;
  const x1 = node.side === 'left' ? CX - RX : CX + RX;
  const y1 = CY + (node.y - CY) * 0.14;
  const dx = endX - x1;
  const dy = node.y - y1;
  const o1 = jitter(index + 1, 24);
  const o2 = jitter(index + 7, 20);
  return `M ${x1.toFixed(1)} ${y1.toFixed(1)} C ${(x1 + dx * 0.34).toFixed(1)} ${(y1 + dy * 0.12 + o1).toFixed(1)}, ${(x1 + dx * 0.6).toFixed(1)} ${(y1 + dy * 0.8 - o2).toFixed(1)}, ${endX} ${node.y}`;
}
export function TasteMap() {
  const reduced = useReducedMotion();
  const draw = (delay: number) => reduced ? {
    pathLength: 1,
    opacity: 1
  } : {
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: {
        duration: 0.55,
        delay,
        ease: 'easeOut' as const
      },
      opacity: {
        duration: 0.1,
        delay
      }
    }
  };
  const fade = (delay: number) => reduced ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.25,
      delay,
      ease: [0.23, 1, 0.32, 1] as const
    }
  };
  return <section aria-labelledby="smakkarta" className="border-b border-black/12" data-id="element-641" data-mp="21f8heco63xc">
      <div className="mx-auto max-w-page px-5 py-16 md:px-8 md:py-24" data-id="element-642" data-mp="1zceykcb5snv">
        <Reveal data-id="element-643" data-mp="3u46fc8ynsnpf">
          <div className="max-w-2xl" data-id="element-644" data-mp="3u7uatdnnzk6s">
            <p className="text-[11px] font-bold uppercase tracking-wide3 text-black/40" data-id="element-645" data-mp="3p5lf49jeq40z">Vad vi tittar på</p>
            <h2 id="smakkarta" className="display-xl mt-4 text-4xl uppercase leading-[0.95] sm:text-6xl" data-id="element-646" data-mp="3k8bt73v88ahw">
              Från miljö till mästerverk
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-black/65" data-id="element-647" data-mp="1ifxcu98bze3l">
              Ett bra ställe är aldrig en enda sak. Det är allt på en gång — och allt är gjort med smaken
              i fokus.
            </p>
          </div>
        </Reveal>

        {/* ------------------------------ Desktop map ------------------------------ */}
        <div className="relative mt-10 hidden md:block" data-id="element-648" data-mp="63a00bex8wwc">
          <motion.svg viewBox="0 0 900 836" className="h-auto w-full" initial="hidden" whileInView="shown" viewport={{
          once: true,
          amount: 0.3
        }} role="img" aria-label={`Tankekarta: ${NODES.map(n => n.label).join(', ')}`} data-id="element-649" data-mp="b3fv61keh1j6">
            {/* Hub — the stamp itself */}
            <motion.g variants={{
            hidden: {
              opacity: 0,
              scale: 1.3,
              rotate: -20
            },
            shown: reduced ? {
              opacity: 1,
              scale: 1,
              rotate: 0
            } : {
              opacity: 1,
              scale: 1,
              rotate: 0,
              transition: {
                duration: 0.26,
                delay: 0.1,
                ease: [0.23, 1, 0.32, 1]
              }
            }
          }} style={{
            transformOrigin: `${CX}px ${CY}px`
          }} data-id="element-650" data-mp="3jkspm0cx0byu">
              <g transform={`translate(${CX - 155} ${CY - 78})`} data-id="element-651" data-mp="2qz43ewepntyx">
                <StampBadge width={310} rotate={-4} data-id="element-652" data-mp="34y3ueapp4ky9" />
              </g>
            </motion.g>

            {NODES.map((node, i) => {
            const delay = 0.25 + i * 0.08;
            const anchor = node.side === 'left' ? 'end' : 'start';
            const x = node.side === 'left' ? 280 : 620;
            const dotX = node.side === 'left' ? 292 : 608;
            return <g key={node.label} transform={`rotate(${node.tilt} ${dotX} ${node.y})`} data-id="element-653" data-mp="3nlpdvut8d5fv" data-mpi={i}>
                  <motion.path d={branch(node, i)} fill="none" stroke={node.color} strokeWidth={i % 2 === 0 ? 2.9 : 2.3} strokeLinecap="round" variants={{
                hidden: {
                  pathLength: 0,
                  opacity: 0
                },
                shown: draw(delay)
              }} data-id="element-654" data-mp="3ip8mmrqqiayk" />
                  {/* Hand-drawn tick at the end of each branch */}
                  <motion.path d={`M ${dotX - 7} ${node.y - 6} l 14 12 M ${dotX + 7} ${node.y - 6} l -14 12`} stroke={node.color} strokeWidth="2.6" strokeLinecap="round" fill="none" variants={{
                hidden: {
                  pathLength: 0,
                  opacity: 0
                },
                shown: draw(delay + 0.42)
              }} data-id="element-655" data-mp="8ja0x986ibnq" />
                  <motion.text x={x} y={node.y + 2} textAnchor={anchor} className="font-display" fontSize="36" fill="#111111" variants={{
                hidden: {
                  opacity: 0,
                  y: 7
                },
                shown: fade(delay + 0.44)
              }} data-id="element-656" data-mp="r7ov25dsm5sr">
                    {node.label}
                  </motion.text>
                  <motion.text x={x} y={node.y + 32} textAnchor={anchor} className="font-hand" fontSize="27" fill="rgba(17,17,17,0.5)" variants={{
                hidden: {
                  opacity: 0,
                  y: 7
                },
                shown: fade(delay + 0.54)
              }} data-id="element-657" data-mp="2vfjg0ogyk3sz">
                    {node.sub}
                  </motion.text>
                </g>;
          })}
          </motion.svg>
        </div>

        {/* ------------------------------- Mobile map ------------------------------ */}
        <div className="mt-10 flex justify-center md:hidden" data-id="element-658" data-mp="34g7utle6e86x">
          <motion.div initial={reduced ? false : {
          opacity: 0,
          scale: 1.25,
          rotate: -20
        }} whileInView={{
          opacity: 1,
          scale: 1,
          rotate: 0
        }} viewport={{
          once: true,
          amount: 0.8
        }} transition={{
          duration: 0.26,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-659" data-mp="34ed944v2yc1s">
            <StampBadge className="w-64" data-id="element-660" data-mp="1o0n3gtobbfq1" />
          </motion.div>
        </div>

        <ul className="mt-8 md:hidden" data-id="element-661" data-mp="2dpc23b7u7qjf">
          {NODES.map((node, i) => <motion.li key={node.label} initial={reduced ? false : {
          opacity: 0,
          x: -10
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          amount: 0.6
        }} transition={{
          duration: 0.25,
          delay: Math.min(i * 0.05, 0.2),
          ease: [0.23, 1, 0.32, 1]
        }} className="relative border-l-2 py-4 pl-6" style={{
          borderColor: node.color,
          transform: `rotate(${node.tilt * 0.4}deg)`
        }} data-id="element-662" data-mp="2ipgkj6sb2vca" data-mpi={i}>
              <span className="absolute -left-[5px] top-6 block h-2 w-2 rounded-full" style={{
            backgroundColor: node.color
          }} data-id="element-663" data-mp="2not6lpbcrydt" />
              <p className="font-display text-2xl leading-none" data-id="element-664" data-mp="2rnigv913gzt8">{node.label}</p>
              <p className="mt-1.5 font-hand text-xl text-black/50" data-id="element-665" data-mp="3fe62sy7uhpys">{node.sub}</p>
            </motion.li>)}
        </ul>

        {/* --------------------------------- Stamp -------------------------------- */}
        <Reveal data-id="element-666" data-mp="18oubofc48fan">
          <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-6 border-t border-black/12 pt-10" data-id="element-667" data-mp="13sdktddj7mtv">
            <div className="max-w-xl" data-id="element-668" data-mp="1ihjiuy0mv706">
              <p className="font-display text-2xl leading-snug sm:text-3xl" data-id="element-669" data-mp="1iggr6nvhl0j7">
                Klarar de hela kartan får de stämpeln.
              </p>
              <Note className="mt-3 block" data-id="element-670" data-mp="ps2ymnn62wf1">Vi har varit där. Vi har betalat. Vi står för den.</Note>
            </div>
          </div>
        </Reveal>

        {/* ------------------------------- Closing ------------------------------- */}
        <Reveal data-id="element-671" data-mp="zra7axiyi2vt">
          <div className="mt-14 border-t-2 border-black pt-10" data-id="element-672" data-mp="zrtrkkw2x3d0">
            <p className="max-w-3xl font-display text-[1.8rem] font-light leading-[1.2] sm:text-[2.4rem]" data-id="element-673" data-mp="uqe46m6fikrg">
              Smaken må vara som baken. Men den bästa smaken är inte så delad som man tror.
            </p>
            <p className="display-xl mt-8 text-[2.4rem] uppercase leading-[0.95] sm:text-[4rem]" data-id="element-674" data-mp="265t3vxq0vqq9">
              Småken <span className="text-black/25" data-id="element-675" data-mp="2s67riyjzsdw4">=</span>{' '}
              <span className="relative inline-block text-rod" data-id="element-676" data-mp="2n74lpbw6u3n3">
                Super-smak&shy;kompis
                <UnderlineMark className="absolute -bottom-1 left-0 h-3 w-full text-rod sm:-bottom-2 sm:h-4" data-id="element-677" data-mp="2igehw33eu8ui" />
              </span>
            </p>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-black/65" data-id="element-678" data-mp="2dhl2g54kc7rp">
              Tolv saker ska sitta innan någon får stämpeln. Klarar de allt är de inte bara bra — de är
              värda att tipsa om.
            </p>
          </div>
        </Reveal>
      </div>
    </section>;
}